import type { PartnerWeRunCluster } from '../types/partners';

/**
 * Partner pack versions: what the partner reported, what CurseForge lists and
 * what our servers run.
 *
 * `normVersion` and `versionInDisplayName` are shared algorithms: argus-backend
 * and Mr. Roboto implement them identically, so a version that matches here
 * matches there. Change all three together or not at all.
 */

/** trim → lowercase → drop one leading `v` before a digit → collapse inner whitespace. `" V2.3.2 "` → `"2.3.2"`. */
export function normVersion(version: string): string {
  return version.trim().toLowerCase().replace(/^v(?=\d)/, '').replace(/\s+/g, ' ');
}

const TOKEN_CHAR = /[0-9a-z.]/;

/**
 * Whether a CurseForge file's display name carries this version as a whole
 * token: bounded on both sides by the start/end or by a character that is not
 * `[0-9a-z.]`. One `v` directly before the version is part of the left
 * boundary when that `v` is itself at the start or after such a character.
 * "All the Mods 10-2.3.2" and "ATM10 v2.3.2" carry 2.3.2; "dev2.3.2" does
 * not, and none of them carries 2.3 or 2.3.20.
 */
export function versionInDisplayName(displayName: string, version: string): boolean {
  const name = normVersion(displayName);
  const wanted = normVersion(version);
  if (!wanted) return false;
  const bounded = (char: string | undefined) => char === undefined || !TOKEN_CHAR.test(char);
  for (let at = name.indexOf(wanted); at !== -1; at = name.indexOf(wanted, at + 1)) {
    const left = bounded(name[at - 1]) || (name[at - 1] === 'v' && bounded(name[at - 2]));
    if (left && bounded(name[at + wanted.length])) return true;
  }
  return false;
}

/**
 * A version that can be put in order: dotted numbers such as "2.3.2" or "v10",
 * not "Beta 4" or "2.3.2-hotfix". The same rule argus-backend uses to decide a
 * report is live, so this page never calls a cluster behind when Argus has
 * already marked the report live.
 */
export const isNumberedVersion = (version: string): boolean => /^\d+(\.\d+)*$/.test(normVersion(version));

/**
 * Natural-order compare of two numbered versions, the status monitor's
 * `compareNames` idea made exact: segment by segment, so "2.10" is newer than
 * "2.9" and "2.3" equals "2.3.0". Null when either side is not numbered,
 * because ordering a label against a number would invent an answer.
 */
export function compareVersions(a: string, b: string): number | null {
  if (!isNumberedVersion(a) || !isNumberedVersion(b)) return null;
  const left = normVersion(a).split('.').map(Number);
  const right = normVersion(b).split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
}

/** The same version, however it is spelled: " V2.3 " is 2.3 is 2.3.0. */
export const sameVersion = (a: string, b: string): boolean => normVersion(a) === normVersion(b) || compareVersions(a, b) === 0;

// ── Partner reported · On CurseForge · We run ──────────────────────────────

/**
 * `match`: agrees with the reference. `behind` / `ahead`: older or newer than
 * it. `differs`: different, and the two cannot be put in order. `missing`:
 * there is nothing here. `unknown`: Argus cannot tell.
 */
export type TriadState = 'match' | 'behind' | 'ahead' | 'differs' | 'missing' | 'unknown';

export interface TriadCell {
  value: string | null;
  state: TriadState;
  /** A short phrase that says what the state means for this column. */
  label: string;
}

export interface TriadCluster extends PartnerWeRunCluster {
  state: TriadState;
}

export interface VersionTriad {
  reported: TriadCell;
  curseforge: TriadCell;
  weRun: TriadCell & { clusters: TriadCluster[] };
  /** Clusters running an older version than the partner reported. */
  behindClusters: TriadCluster[];
}

export interface VersionTriadInput {
  /** The version the partner (or staff on their behalf) reported. */
  reported: string | null;
  /** The newest CurseForge file's display name, or the file linked to the report. */
  curseforgeName: string | null;
  /** The pack is linked to Apogee, so CurseForge is being watched at all. */
  curseforgeWatched: boolean;
  /** The report has been matched to a CurseForge file already. */
  curseforgeListed?: boolean;
  /** The last CurseForge check failed. */
  curseforgeError?: string | null;
  weRun: PartnerWeRunCluster[];
}

function clusterState(version: string | null, reference: string | null, curseforgeName: string | null): TriadState {
  if (!version) return 'unknown';
  if (reference) {
    if (sameVersion(version, reference)) return 'match';
    const order = compareVersions(version, reference);
    if (order === null) return 'differs';
    return order < 0 ? 'behind' : 'ahead';
  }
  if (curseforgeName) return versionInDisplayName(curseforgeName, version) ? 'match' : 'differs';
  return 'unknown';
}

/**
 * Lines the three sources up against the partner's report. With no report,
 * our clusters are checked against the newest CurseForge file name instead,
 * which can say "matches" but never "behind", because a display name is not
 * a version.
 */
export function versionTriad(input: VersionTriadInput): VersionTriad {
  const reported = input.reported?.trim() ? input.reported.trim() : null;

  const reportedCell: TriadCell = reported
    ? { value: reported, state: 'match', label: 'Reported by the partner' }
    : { value: null, state: 'missing', label: 'Nothing reported yet' };

  let curseforge: TriadCell;
  if (!input.curseforgeWatched) {
    curseforge = { value: null, state: 'unknown', label: 'Link the pack to Apogee to watch CurseForge' };
  } else if (input.curseforgeError && !input.curseforgeName) {
    curseforge = { value: null, state: 'unknown', label: 'The last CurseForge check failed' };
  } else if (!input.curseforgeName) {
    curseforge = { value: null, state: 'missing', label: 'No CurseForge files seen yet' };
  } else if (!reported) {
    curseforge = { value: input.curseforgeName, state: 'match', label: 'Newest file on CurseForge' };
  } else if (input.curseforgeListed || versionInDisplayName(input.curseforgeName, reported)) {
    curseforge = { value: input.curseforgeName, state: 'match', label: 'The reported version is listed' };
  } else {
    curseforge = { value: input.curseforgeName, state: 'differs', label: 'The reported version is not listed yet' };
  }

  const clusters: TriadCluster[] = input.weRun.map((cluster) => ({
    ...cluster,
    state: clusterState(cluster.version, reported, reported ? null : input.curseforgeName),
  }));
  const versions = [...new Set(clusters.map((c) => c.version).filter((v): v is string => !!v))];
  const behindClusters = clusters.filter((c) => c.state === 'behind');

  let weRun: TriadCell;
  if (clusters.length === 0) {
    weRun = { value: null, state: 'missing', label: 'No cluster runs this pack' };
  } else if (versions.length === 0) {
    weRun = { value: null, state: 'unknown', label: 'No version recorded for our clusters' };
  } else {
    const value = versions.join(', ');
    const states = new Set(clusters.map((c) => c.state));
    if (states.has('behind')) {
      weRun = { value, state: 'behind', label: behindClusters.length === clusters.length ? 'Older than reported' : `${behindClusters.length} of ${clusters.length} clusters are older than reported` };
    } else if ([...states].every((s) => s === 'match')) {
      weRun = { value, state: 'match', label: reported ? 'Up to date' : 'Matches CurseForge' };
    } else if (states.has('ahead') && [...states].every((s) => s === 'ahead' || s === 'match')) {
      weRun = { value, state: 'ahead', label: 'Newer than reported' };
    } else if (states.has('differs')) {
      weRun = { value, state: 'differs', label: reported ? 'Different from the reported version' : 'Not the newest CurseForge file name' };
    } else {
      weRun = { value, state: 'unknown', label: 'Cannot compare with the report' };
    }
  }

  return { reported: reportedCell, curseforge, weRun: { ...weRun, clusters }, behindClusters };
}

/** Where to bump a cluster's version: the pack's Modpack Configs page, on its Cluster section. */
export const clusterUpdatePath = (headAddress: string): string =>
  `/modpack-configs/${encodeURIComponent(headAddress)}?section=cluster`;
